import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Chip, Button, Card, CardContent, Grid, IconButton, Menu, MenuItem, Stepper, Step, StepLabel, Dialog, DialogTitle, DialogContent, DialogActions, ToggleButtonGroup, ToggleButton, TextField, CircularProgress } from '@mui/material';
import { PiDownloadSimple, PiDotsThreeVertical, PiPencilSimple, PiTrash, PiChatCircle, PiCloudArrowUp, PiCode, PiList, PiPlay } from 'react-icons/pi';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/apiService';
import { schemaService, WorkflowUiSchema } from '../services/schemaService';
import RenameJobDialog from './RenameJobDialog';
import ThemeLoader from './ThemeLoader';
import { DocumentThumbnail } from './DocumentThumbnail';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import {
  filterOutputFiles,
  getDownloadFilename,
  isMultiStepWorkflow as checkMultiStepWorkflow,
  shouldShowReviewForm,
  getCompletionActions,
  getReviewFormConfig,
  getExecutionSteps as getSchemaExecutionSteps,
  getStepControls,
  getStepDisplayConfig,
  isUnifiedWorkflowEnabled,
  getUnifiedWorkflowConfig,
  allowsJsonDownload,
  getAllOutputFiles,
  getInputDisplayConfig,
  shouldShowOriginalDocuments,
} from '../utils/workflowSchemaHelpers';

import WorkflowReviewForm from './WorkflowReviewForm';
import PodcastPlayer from './PodcastPlayer';
import WorkflowResultData from './WorkflowResultData';
import { normalizeWorkflowData } from '../utils/workflowDataParsing';

// PDF Viewer component that handles signed URL fetching
function PDFViewer({ status }: { status: any }) {
  const [signedUrls, setSignedUrls] = useState<Map<string, string>>(new Map());
  const [activeFileIndex, setActiveFileIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfFiles, setPdfFiles] = useState<{ uri: string; label: string }[]>([]);

  React.useEffect(() => {
    const fetchFiles = async () => {
      // V1: flat string array at input.input_file_s3_uris
      const inputFiles = status?.input?.input_file_s3_uris || [];
      // V2: object array at input.workflow_data.input_files / .files with {s3_uri, identifier}
      const v2Raw = status?.input?.workflow_data?.input_files || status?.input?.workflow_data?.files || [];
      const v2InputFiles = v2Raw.map((f: any) => typeof f === 'string' ? { uri: f } : { uri: f?.s3_uri, identifier: f?.identifier });
      
      // Build list of viewable files with labels
      const viewable: { uri: string; label: string }[] = [];
      const seen = new Set<string>();
      
      for (const uri of inputFiles) {
        if (uri && !seen.has(uri)) {
          seen.add(uri);
          const filename = uri.split('/').pop() || 'Document';
          viewable.push({ uri, label: filename });
        }
      }
      for (const f of v2InputFiles) {
        if (f.uri && !seen.has(f.uri)) {
          seen.add(f.uri);
          const label = f.identifier
            ? f.identifier.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
            : f.uri.split('/').pop() || 'Document';
          viewable.push({ uri: f.uri, label });
        }
      }
      
      if (viewable.length === 0) {
        setError('No input documents found');
        return;
      }
      
      setPdfFiles(viewable);
      setActiveFileIndex(0);
      
      // Fetch signed URL for the first file
      setLoading(true);
      try {
        const response = await apiService.getSignedViewUrl!(viewable[0].uri);
        setSignedUrls(new Map([[viewable[0].uri, response.signedUrl]]));
      } catch (err: any) {
        console.error('Failed to get signed URL:', err);
        if (err.response?.status === 404 || err.message?.includes('File not found')) {
          setError('Document not found in storage');
        } else {
          setError('Failed to load document');
        }
      } finally {
        setLoading(false);
      }
    };

    if (status) {
      fetchFiles();
    }
  }, [status]);

  const handleFileSwitch = async (index: number) => {
    const file = pdfFiles[index];
    if (!file) return;
    setActiveFileIndex(index);
    
    // Already cached
    if (signedUrls.has(file.uri)) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getSignedViewUrl!(file.uri);
      setSignedUrls(prev => new Map(prev).set(file.uri, response.signedUrl));
    } catch (err: any) {
      console.error('Failed to get signed URL:', err);
      setError('Failed to load document');
    } finally {
      setLoading(false);
    }
  };

  const activeUrl = pdfFiles[activeFileIndex] ? signedUrls.get(pdfFiles[activeFileIndex].uri) : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {pdfFiles.length > 1 && (
        <Box sx={{ display: 'flex', gap: 0.5, p: 1, borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap' }}>
          {pdfFiles.map((file, i) => (
            <Button
              key={file.uri}
              size="small"
              variant={i === activeFileIndex ? 'contained' : 'outlined'}
              onClick={() => handleFileSwitch(i)}
              sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, minWidth: 0 }}
            >
              {file.label}
            </Button>
          ))}
        </Box>
      )}
      <Box sx={{ flex: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography>Loading document...</Typography>
          </Box>
        ) : error ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            {error}
          </Typography>
        ) : activeUrl ? (
          <iframe
            src={activeUrl}
            style={{ width: '100%', height: '100%', border: 'none' }}
            title={pdfFiles[activeFileIndex]?.label || 'Original Document'}
          />
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No document available
          </Typography>
        )}
      </Box>
    </Box>
  );
}

interface SimpleJobDisplayProps {
  job: {
    jobId: string;
    executionArn?: string;
    status: string;
    createdAt: string;
    startedAt?: string;
    displayName?: string;
    number?: string;
    practice?: string;
    workflows?: string[];
    input?: {
      number: string;
    };
    // V2 workflow fields
    stateMachineVersion?: 'v1' | 'v2';
    stateMachineType?: string;
    processingType?: string;
  };
  onRename?: (jobId: string, newName: string) => void;
  onDelete?: (jobId: string) => void;
}

export default function SimpleJobDisplay({ job, onRename, onDelete }: SimpleJobDisplayProps) {
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [jsonEditorOpen, setJsonEditorOpen] = useState(false);
  const [jsonData, setJsonData] = useState<any>(null);
  const [workflowUiSchema, setWorkflowUiSchema] = useState<WorkflowUiSchema | null>(null);
  // Track current execution ARN (may change for chained workflows)
  const [currentExecutionArn, setCurrentExecutionArn] = useState(job.executionArn || job.jobId);
  // Track if step 2 is running (after manual trigger)
  const [stepTwoRunning, setStepTwoRunning] = useState(false);
  // File input ref for JSON upload
  const fileInputRef = useRef<HTMLInputElement>(null);
  // View mode for review dialog: 'form' or 'json'
  const [viewMode, setViewMode] = useState<'form' | 'json'>('form');
  // Raw JSON text for direct editing
  const [jsonText, setJsonText] = useState<string>('');
  // JSON parse error message
  const [jsonError, setJsonError] = useState<string | null>(null);
  // S3 URI of the loaded output file (for saving edits back)
  const [sourceS3Uri, setSourceS3Uri] = useState<string | null>(null);
  // save-in-progress and success feedback
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  const aiChatEnabled = process.env.REACT_APP_AI_CHAT_ENABLED === 'true';
  
  // Poll frequently when job is running or podcast is still generating
  const { data: status } = useQuery({
    queryKey: ['execution', currentExecutionArn],
    queryFn: () => apiService.getExecutionStatus(currentExecutionArn),
    refetchInterval: (query) => {
      const data = query.state.data as any;
      if (job.status === 'RUNNING') return 2000;
      if (data?.podcast?.status === 'processing') return 3000;
      return 30000;
    },
    enabled: !!currentExecutionArn,
  });
  
  // Get the actual execution status from the polled data
  const actualExecutionStatus = (status as any)?.status || job.status;
  
  // Update stepTwoRunning based on actual status
  useEffect(() => {
    if (stepTwoRunning && (actualExecutionStatus === 'SUCCEEDED' || actualExecutionStatus === 'FAILED')) {
      setStepTwoRunning(false);
    }
  }, [actualExecutionStatus, stepTwoRunning]);

  // Handle chained workflow execution ARN changes
  useEffect(() => {
    const statusData = status as any;
    if (statusData?.newExecutionArn && statusData.newExecutionArn !== currentExecutionArn) {
      console.log('Chained workflow: Switching to new execution ARN:', statusData.newExecutionArn);
      setCurrentExecutionArn(statusData.newExecutionArn);
    }
  }, [status, currentExecutionArn]);

  // Get practice and workflow from job (preferred) or status (fallback)
  // Use job.workflows for display since it has the original unified workflow name
  // (e.g., "airplane-lease" instead of "airplane-lease-step-one")
  const practice = job.practice || (status as any)?.input?.practice || '';
  const workflows = job.workflows || (status as any)?.input?.workflows || [];
  const currentWorkflow = workflows[0] || '';
  
  // For UI schema loading, we need the actual step workflow (e.g., "ocean-bank-step-one")
  // not the unified workflow name (e.g., "ocean-bank")
  const actualWorkflow = (job as any).actualWorkflows?.[0] || 
                         (status as any)?.chainedWorkflow?.currentWorkflow ||
                         (status as any)?.input?.workflows?.[0] ||
                         currentWorkflow;

  // Check if this workflow has a UI schema (for multi-step workflows)
  useEffect(() => {
    const checkUiSchema = async () => {
      if (!practice || !actualWorkflow) return;
      
      try {
        // Use actualWorkflow (the step workflow) to fetch the correct UI schema
        const response = await schemaService.getWorkflowUiSchema(practice, actualWorkflow);
        if (response) {
          setWorkflowUiSchema(response.uiSchema);
        }
      } catch (error) {
        console.log('No UI schema available for workflow:', actualWorkflow);
        setWorkflowUiSchema(null);
      }
    };
    
    checkUiSchema();
  }, [practice, actualWorkflow]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'RUNNING': return 'primary';
      case 'SUCCEEDED': return 'success';
      case 'FAILED': return 'error';
      case 'WAITING_FOR_INPUT': return 'info';
      default: return 'default';
    }
  };

  const getDisplayStatus = (status: string) => {
    return status === 'SUCCEEDED' ? 'COMPLETED' : status;
  };

  // Schema-driven helpers (replace hardcoded workflow checks)
  const completionActions = getCompletionActions(workflowUiSchema);
  const reviewFormConfig = getReviewFormConfig(workflowUiSchema);
  const stepControls = getStepControls(workflowUiSchema);
  const stepDisplayConfig = getStepDisplayConfig(workflowUiSchema);
  const unifiedWorkflowConfig = getUnifiedWorkflowConfig(workflowUiSchema);
  const isUnifiedWorkflow = isUnifiedWorkflowEnabled(workflowUiSchema);
  const canDownloadJson = allowsJsonDownload(workflowUiSchema);
  const inputDisplayConfig = getInputDisplayConfig(workflowUiSchema);
  const showOriginalDocs = shouldShowOriginalDocuments(workflowUiSchema);
  
  // Check if this is a multi-step workflow that needs a review screen
  const isMultiStepWorkflow = () => {
    return checkMultiStepWorkflow(workflowUiSchema);
  };
  
  // Check if the review screen should be shown
  const canShowReviewScreen = () => {
    const executionStatus = (status as any)?.status || job.status;
    const chainedInfo = (status as any)?.chainedWorkflow;
    // Only hide review if backend is auto-chaining (autoChain: true)
    // For workflows with autoChain: false, we want to show the review screen
    const isBackendAutoChaining = chainedInfo?.autoChain === true;
    
    return shouldShowReviewForm(workflowUiSchema, executionStatus, isBackendAutoChaining);
  };
  
  // Get review screen text from schema (use helpers)
  const getReviewScreenTitle = () => {
    return reviewFormConfig?.title || 
           completionActions.completionMessage || 
           'Step Complete';
  };
  
  const getReviewScreenDescription = () => {
    return reviewFormConfig?.description || 
           'Review and complete the extracted data to continue';
  };
  
  const getReviewButtonText = () => {
    return completionActions.reviewButtonText || 
           reviewFormConfig?.submitButtonText ||
           'Review & Continue';
  };
  
  const getContinueButtonText = () => {
    return stepControls.triggerButtonText || 
           completionActions.continueButtonText || 
           'Continue to Next Step';
  };
  
  const getFilteredFiles = () => {
    const allFiles = (status as any)?.output?.output_files || [];
    const isV2 = Boolean((status as any)?.input?.metadata);
    return filterOutputFiles(allFiles, workflowUiSchema, { isV2 });
  };
  
  const outputFiles = getFilteredFiles();

  // Get all files including hidden ones (for manual download feature)
  const getAllFiles = () => {
    return (status as any)?.output?.output_files || [];
  };

  // Inline data results: steps like cortese_list return data (count/matches/summary) with no files.
  // pull those non-file step outputs out of workflow_data so we can render them inline.
  const getStepData = (): Record<string, any> | undefined => {
    // workflow_data may arrive as an object OR as a JSON string (large payloads), and Python
    // handlers can emit bare NaN which is invalid JSON — normalizeWorkflowData handles both so
    // one bad field can't blank the whole result.
    const wd = normalizeWorkflowData((status as any)?.output?.workflow_data);
    if (!wd) return undefined;
    const skip = ['files', 'input_files', 'output_files', 'comment_workflow_data'];
    // top-level addresses[] is the user's input list, not a step result — surface it to the
    // cortese view so addresses with zero matches are still shown as "checked".
    const searchedAddresses: string[] = Array.isArray(wd.addresses) ? wd.addresses : [];
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(wd)) {
      if (skip.includes(k)) continue;
      // only object (non-array) values are step results; arrays like addresses are inputs
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        const { params, ...rest } = v as any;
        if (Object.keys(rest).length > 0) {
          if (Array.isArray((rest as any).matches) && searchedAddresses.length > 0) {
            (rest as any).searched_addresses = searchedAddresses;
          }
          out[k] = rest;
        }
      }
    }
    return Object.keys(out).length > 0 ? out : undefined;
  };
  const stepData = getStepData();
  
  // Get JSON file specifically for download/edit functionality
  const getJsonOutputFile = () => {
    const allFiles = getAllFiles();
    return allFiles.find((file: any) => {
      const fileType = file.s3_uri ? file.s3_uri.split('.').pop()?.toLowerCase() : file.name?.split('.').pop()?.toLowerCase();
      return fileType === 'json';
    });
  };

  // get execution steps — use V2 progress, then schema, then backend chained info
  const getExecutionSteps = () => {
    const v2Progress = (status as any)?.v2Progress;
    if (v2Progress?.stepNames?.length > 0) {
      return [
        'Preparing',
        ...v2Progress.stepNames.map((name: string) =>
          name.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
        ),
        'Complete'
      ];
    }
    const chainedInfo = (status as any)?.chainedWorkflow;
    return getSchemaExecutionSteps(workflowUiSchema, chainedInfo);
  };

  const executionSteps = getExecutionSteps();

  const getCurrentStep = () => {
    const v2Progress = (status as any)?.v2Progress;
    const chainedInfo = (status as any)?.chainedWorkflow;
    const currentStatus = actualExecutionStatus;
    
    if (currentStatus === 'SUCCEEDED' || currentStatus === 'COMPLETED') {
      return executionSteps.length;
    }
    if (currentStatus === 'FAILED') return -1;
    if (currentStatus === 'WAITING_FOR_INPUT') return Math.floor(executionSteps.length / 2);
    
    // use real V2 progress when available
    if (v2Progress && v2Progress.totalSteps > 0) {
      // +1 because "Preparing" is step 0, workflow steps start at index 1
      return Math.min(v2Progress.currentStepIndex + 1, executionSteps.length - 1);
    }
    
    // For chained workflows, calculate step based on current step in chain
    if (chainedInfo?.isChainedWorkflow) {
      const currentStepInChain = chainedInfo.currentStep || 1; // 1-indexed
      const baseStep = (status as any)?.step || 0;
      
      if (currentStepInChain === 1) {
        // In first step of chain
        return Math.min(baseStep + 1, 3); // Steps 1-3 for step-one
      } else if (currentStepInChain === 2) {
        // In second step of chain
        return Math.min(baseStep + 3, executionSteps.length - 1); // Steps 4-6 for step-two
      }
    }
    
    return Math.min((status as any)?.step || 0, executionSteps.length - 1);
  };

  // Use schema-driven download filename
  const getDownloadFileName = (file: any) => {
    return getDownloadFilename(file, workflowUiSchema, {
      jobNumber: (status as any)?.input?.number || job.number || (job as any).input?.number || (status as any)?.input?.execution_metadata?.original_input?.number,
      workflow: currentWorkflow,
      practice: practice,
    });
  };
  

  
  const handleEditData = async () => {
    try {
      // Look in ALL files (not filtered outputFiles)
      // Use findLast to prefer the final step's output (V2 files are ordered by step)
      const allFiles = (status as any)?.output?.output_files || [];
      const jsonFiles = allFiles.filter((file: any) => {
        const fileType = file.s3_uri ? file.s3_uri.split('.').pop()?.toLowerCase() : file.name?.split('.').pop()?.toLowerCase();
        return fileType === 'json';
      });
      const jsonFile = jsonFiles[jsonFiles.length - 1];
      

      
      if (!jsonFile) {
        console.error('No JSON file found in output files');
        alert('No JSON data found to edit.');
        return;
      }
      
      // track where the file lives so we can save edits back
      setSourceS3Uri(jsonFile.s3_uri || null);
      setSaveSuccess(false);
      
      // Get the JSON content
      let content;
      if (jsonFile.url) {
        const response = await fetch(jsonFile.url);
        content = await response.text();
      } else if (jsonFile.s3_uri) {
        content = await apiService.getFileContent(jsonFile.s3_uri);
      }

      const parseJsonContent = (raw: unknown) => {
        if (typeof raw !== 'string') {
          return raw;
        }

        try {
          return JSON.parse(raw);
        } catch (primaryError) {
          const trimmed = raw.trim();
          const looksBase64 = /^[A-Za-z0-9+/=\s]+$/.test(trimmed) && trimmed.length % 4 === 0;

          if (!looksBase64) {
            throw primaryError;
          }

          try {
            const decoded = (() => {
              if (typeof window !== 'undefined' && typeof window.atob === 'function') {
                return window.atob(trimmed);
              }
              if (typeof globalThis !== 'undefined' && typeof (globalThis as any).atob === 'function') {
                return (globalThis as any).atob(trimmed);
              }
              if (typeof Buffer !== 'undefined') {
                return Buffer.from(trimmed, 'base64').toString('utf-8');
              }
              throw new Error('No base64 decoder available');
            })();
            return JSON.parse(decoded);
          } catch (secondaryError) {
            console.error('Failed to decode base64 JSON content', secondaryError);
            throw primaryError;
          }
        }
      };

      const jsonData = parseJsonContent(content);
      
      // Extract the actual data from the responses array
      // Unwrap common response wrappers: { responses: [...] } or { final_response: {...} }
      // Guard: only unwrap final_response if it's an object (not a string from intermediate steps)
      const actualData = jsonData.responses?.[0]
        || (jsonData.final_response && typeof jsonData.final_response === 'object' ? jsonData.final_response : null)
        || jsonData;
      setJsonData(actualData);
      setJsonEditorOpen(true);
    } catch (error) {
      console.error('Failed to load data:', error);
      alert('Failed to load loan data.');
    }
  };

  const handleSaveReview = async () => {
    if (!sourceS3Uri || !jsonData) return;
    setSaving(true);
    setSaveSuccess(false);
    try {
      await apiService.saveFileContent(sourceS3Uri, JSON.stringify(jsonData, null, 2));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error: any) {
      console.error('Failed to save review data:', error);
      const detail = error?.message || 'Unknown error';
      alert(`Failed to save changes: ${detail}`);
    } finally {
      setSaving(false);
    }
  };
  
  const handleSubmitToStepTwo = async () => {
    try {
      if (!jsonData) {
        alert('Data is not loaded yet. Please try again.');
        return;
      }
      
      // Get next workflow from UI schema (new format or legacy)
      const nextWorkflowConfig = workflowUiSchema?.multi_step_config?.next_workflow;
      const legacyNextWorkflow = workflowUiSchema?.continuation?.next_workflow;
      
      const nextPractice = nextWorkflowConfig?.practice || practice;
      const nextWorkflow = nextWorkflowConfig?.workflow || legacyNextWorkflow;
      
      if (!nextWorkflow) {
        alert('No continuation workflow defined');
        return;
      }

      const jobNumber = (status as any)?.input?.number 
        || job.number 
        || (job as any).input?.number
        || (status as any)?.input?.execution_metadata?.original_input?.number
        || currentWorkflow;

      // V1 AI Processor expects {responses: [...]} wrapping, V2 JSONata expects raw JSON
      const isV1 = !currentExecutionArn?.includes('mister-fusion-v2');
      const saveResult = await apiService.saveJsonToS3(jsonData, jobNumber, {
        practice: nextPractice,
        wrapInResponses: isV1,
      });

      // Store the parent workflow for job grouping (appears on unified page)
      // But execute the actual step workflow recipe
      const parentWorkflowName = currentWorkflow;
      
      const workflowData = {
        practice: nextPractice,
        workflows: [nextWorkflow], // Use actual step workflow for execution (e.g., "ocean-bank-step-two")
        number: `${jobNumber}-step-two`,
        input_file_s3_uris: [saveResult.s3Uri],
        workflow_data: {
          isStepTwo: true,
          parentWorkflow: parentWorkflowName, // For job grouping on unified page
          stepWorkflow: nextWorkflow,
        }
      };
      
      const result = await apiService.executeWorkflow(workflowData);
      setJsonEditorOpen(false);
      
      // Step 2 is now a separate job - it will appear in the job queue
      // This step 1 job remains SUCCEEDED, step 2 will show its own status
      if (result?.executionArn) {
        // Trigger a jobs refresh by invalidating the query
        // The new step-two job will appear in the grouped job list
        window.location.reload(); // Simple approach: reload to see the new grouped job
      }
    } catch (error) {
      console.error('Failed to start next step:', error);
      // Keep error alert for actual failures
      alert('Failed to start the next workflow step.');
      setStepTwoRunning(false);
    }
  };

  // Trigger step 2 with output files (for non-JSON workflows like airplane lease)
  const handleTriggerStepTwoWithFiles = async () => {
    try {
      const allFiles = getAllFiles();
      if (!allFiles || allFiles.length === 0) {
        alert('No output files available to pass to next step.');
        return;
      }
      
      // Get next workflow from UI schema
      const nextWorkflowConfig = workflowUiSchema?.multi_step_config?.next_workflow;
      const nextPractice = nextWorkflowConfig?.practice || practice;
      const nextWorkflow = nextWorkflowConfig?.workflow;
      
      if (!nextWorkflow) {
        alert('No continuation workflow defined');
        return;
      }

      const jobNumber = (status as any)?.input?.number 
        || job.number 
        || (job as any).input?.number
        || (status as any)?.input?.execution_metadata?.original_input?.number
        || currentWorkflow;
      const parentWorkflowName = currentWorkflow;
      
      // Get S3 URIs from output files
      const inputUris = allFiles
        .filter((f: any) => f.s3_uri)
        .map((f: any) => f.s3_uri);
      
      if (inputUris.length === 0) {
        alert('No valid output files found.');
        return;
      }
      
      const workflowData = {
        practice: nextPractice,
        workflows: [nextWorkflow],
        number: `${jobNumber}-step-two`,
        input_file_s3_uris: inputUris,
        workflow_data: {
          isStepTwo: true,
          parentWorkflow: parentWorkflowName,
          stepWorkflow: nextWorkflow
        }
      };
      
      const result = await apiService.executeWorkflow(workflowData);
      
      if (result?.executionArn) {
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to start next step:', error);
      alert('Failed to start the next workflow step.');
    }
  };

  const handleDownload = async (file: any) => {
    try {
      if (file.s3_uri) {
        const downloadName = getDownloadFileName(file);
        const signedUrl = await apiService.getSignedDownloadUrl(file.s3_uri, downloadName);
        const link = document.createElement('a');
        link.href = signedUrl;
        link.download = downloadName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  // Handler for downloading JSON data (intermediate output)
  const handleDownloadJson = async () => {
    try {
      const jsonFile = getJsonOutputFile();
      if (!jsonFile) {
        alert('No JSON data file found.');
        return;
      }
      
      const jobNumber = (status as any)?.input?.number 
        || job.number 
        || (job as any).input?.number
        || (status as any)?.input?.execution_metadata?.original_input?.number
        || currentWorkflow;
      const downloadName = `${jobNumber}-extracted-data.json`;
      const signedUrl = await apiService.getSignedDownloadUrl(jsonFile.s3_uri, downloadName);
      const link = document.createElement('a');
      link.href = signedUrl;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('JSON download failed:', error);
      alert('Failed to download JSON data.');
    }
  };

  // Handler for uploading JSON file to trigger step 2
  const handleUploadJsonClick = () => {
    fileInputRef.current?.click();
  };

  const handleJsonFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const parsedData = JSON.parse(text);
      
      // Extract the actual data from the responses array if present
      const actualData = parsedData.responses && parsedData.responses[0] ? parsedData.responses[0] : parsedData;
      
      // Set the data and open editor for review before triggering step 2
      setJsonData(actualData);
      setJsonEditorOpen(true);
    } catch (error) {
      console.error('Failed to parse JSON file:', error);
      alert('Invalid JSON file. Please upload a valid JSON file.');
    }
    
    // Reset file input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const [zipping, setZipping] = useState(false);

  const handleDownloadAll = async () => {
    if (outputFiles.length === 0) return;
    setZipping(true);
    try {
      const zip = new JSZip();
      for (const file of outputFiles) {
        if (!file.s3_uri) continue;
        try {
          const downloadName = getDownloadFileName(file);
          // proxy through our backend to avoid S3 CORS issues
          const blob = await apiService.downloadFileBlob!(file.s3_uri);
          zip.file(downloadName, blob);
        } catch (err) {
          console.warn('failed to fetch file for zip:', file.s3_uri, err);
        }
      }
      const content = await zip.generateAsync({ type: 'blob' });
      const zipName = `${jobName || 'output'}-files.zip`;
      saveAs(content, zipName);
    } catch (err) {
      console.error('zip download failed:', err);
      alert('Failed to create ZIP. Please try again.');
    } finally {
      setZipping(false);
    }
  };

  const [chatLoading, setChatLoading] = useState(false);
  
  const handleChatWithFiles = async () => {
    setChatLoading(true);
    try {
      // Create a new conversation
      const response = await apiService.createChatConversation!(
        `Chat about ${jobName}`
      );
      
      console.log('SimpleJobDisplay - createChatConversation response:', response);
      const conversationId = response.conversation?.conversationId;
      
      if (!conversationId) {
        console.error('SimpleJobDisplay - No conversationId in response');
        alert('Failed to get conversation ID.');
        return;
      }
      
      // Prepare file attachments from output files
      const fileAttachments = outputFiles
        .filter((file: any) => file.s3_uri) // Only include files with S3 URIs
        .map((file: any) => ({
          fileName: getDownloadFileName(file),
          s3Uri: file.s3_uri,
          fileType: file.s3_uri ? file.s3_uri.split('.').pop()?.toLowerCase() || 'unknown' : 'unknown',
          fileSize: file.size || 0
        }));
      
      console.log('SimpleJobDisplay - output files:', outputFiles);
      console.log('SimpleJobDisplay - attaching files:', fileAttachments);
      
      if (fileAttachments.length === 0) {
        console.warn('SimpleJobDisplay - No files with S3 URIs found to attach');
      }
      
      // Navigate with file attachments as URL parameters
      const fileParams = fileAttachments.map((file: any) => 
        `file=${encodeURIComponent(JSON.stringify(file))}`
      ).join('&');
      
      const chatUrl = `/chat?conversation=${conversationId}${fileParams ? '&' + fileParams : ''}`;
      console.log('SimpleJobDisplay - navigating to:', chatUrl);
      window.location.href = chatUrl;
      
    } catch (error) {
      console.error('Failed to create chat:', error);
      alert('Failed to start chat with files.');
    } finally {
      setChatLoading(false);
    }
  };

  const jobName = job.displayName || job.number || job.input?.number || (status as any)?.input?.execution_metadata?.original_input?.number || 'Unknown Job';
  const startDate = new Date(job.startedAt || job.createdAt).toLocaleString();
  
  // get input file URIs for thumbnail - check both V1 and V2 formats
  const v1InputFiles: string[] = (status as any)?.input?.input_file_s3_uris || [];
  const v2Raw: any[] = (status as any)?.input?.workflow_data?.input_files || (status as any)?.input?.workflow_data?.files || [];
  const v2InputFiles: string[] = v2Raw.map((f: any) => typeof f === 'string' ? f : (f?.s3_uri || '')).filter(Boolean);
  const inputFiles = v1InputFiles.length > 0 ? v1InputFiles : v2InputFiles;
  const firstPdfFile = inputFiles.find((uri: string) => uri.toLowerCase().endsWith('.pdf'));

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        {/* Header with job info */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          {/* Thumbnail and Job Info */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            {/* Document Thumbnail */}
            {firstPdfFile && (
              <Box sx={{ flexShrink: 0 }}>
                <DocumentThumbnail 
                  s3Uri={firstPdfFile}
                  width={60}
                  height={78}
                />
              </Box>
            )}
            <Box>
              <Typography variant="h6">{jobName}</Typography>
              <Typography variant="body2" color="text.secondary">
                Started: {startDate}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Chip 
              label={getDisplayStatus(actualExecutionStatus)} 
              color={getStatusColor(actualExecutionStatus)} 
            />
            {outputFiles.length > 0 && !isMultiStepWorkflow() && (
              <>
                {aiChatEnabled && (
                  <Button
                    variant="outlined"
                    startIcon={<PiChatCircle />}
                    onClick={handleChatWithFiles}
                    disabled={chatLoading}
                    size="small"
                  >
                    {chatLoading ? 'Starting Chat...' : 'Chat with Files'}
                  </Button>
                )}
                <Button
                  variant="contained"
                  startIcon={zipping ? <CircularProgress size={16} color="inherit" /> : <PiDownloadSimple />}
                  onClick={handleDownloadAll}
                  disabled={zipping}
                  size="small"
                >
                  {zipping ? 'Zipping...' : 'Download All'}
                </Button>
              </>
            )}
            {(onRename || onDelete) && (
              <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
                <PiDotsThreeVertical size={18} />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Multi-Step Workflow Progress Indicator */}
        {isMultiStepWorkflow() && (
          <Box sx={{ mb: 3, mt: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Workflow Progress
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Step 1 */}
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: (status as any)?.chainedWorkflow?.currentStep === 2 ? 'success.light' : 
                           job.status === 'SUCCEEDED' || job.status === 'WAITING_FOR_INPUT' ? 'primary.main' : 
                           job.status === 'RUNNING' ? 'primary.light' : 'action.disabledBackground',
                  color: (job.status === 'SUCCEEDED' || job.status === 'WAITING_FOR_INPUT') ? 'primary.contrastText' :
                         (job.status === 'RUNNING' || (status as any)?.chainedWorkflow?.currentStep === 2) ? 'primary.main' : 'text.secondary'
                }}
              >
                <Typography variant="body2" fontWeight="bold">1</Typography>
                <Typography variant="body2">
                  {workflowUiSchema?.metadata?.display_name?.replace(/Step (One|Two)/i, '').trim() || 'Extract Data'}
                </Typography>
              </Box>
              
              {/* Arrow */}
              <Typography color="text.secondary">→</Typography>
              
              {/* Step 2 */}
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: (status as any)?.chainedWorkflow?.currentStep === 2 ? 
                           (job.status === 'RUNNING' ? 'primary.light' : 
                            job.status === 'SUCCEEDED' ? 'success.light' : 'primary.main') : 'action.disabledBackground',
                  color: (status as any)?.chainedWorkflow?.currentStep === 2 ?
                         (job.status === 'RUNNING' ? 'primary.main' : 'primary.contrastText') : 'text.secondary'
                }}
              >
                <Typography variant="body2" fontWeight="bold">2</Typography>
                <Typography variant="body2">
                  {workflowUiSchema?.multi_step_config?.next_workflow?.workflow?.replace(/-/g, ' ').replace(/step two/i, '').trim() || 'Generate Documents'}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {/* Debug info for multi-step workflows */}
        {isMultiStepWorkflow() && process.env.NODE_ENV === 'development' && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="caption" display="block">
              Debug Info (Multi-Step Workflow):
            </Typography>
            <Typography variant="caption" display="block">
              Workflow: {currentWorkflow} | Next: {workflowUiSchema?.continuation?.next_workflow || 'none'}
            </Typography>
            <Typography variant="caption" display="block">
              Step Controls: allowDownload={String(stepControls.allowIntermediateDownload)} allowEdit={String(stepControls.allowIntermediateEdit)} allowManualTrigger={String(stepControls.allowManualTrigger)}
            </Typography>
            <Typography variant="caption" display="block">
              All files: {JSON.stringify((status as any)?.output?.output_files?.map((f: any) => ({ name: f.name, s3_uri: f.s3_uri })) || [])}
            </Typography>
          </Box>
        )}

        {/* Hidden file input for JSON upload */}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept=".json,application/json"
          onChange={handleJsonFileSelected}
        />

        {/* Multi-Step Workflow Controls (schema-driven) */}
        {canShowReviewScreen() && (
          <Box sx={{ py: 3, px: 2, bgcolor: 'background.default', borderRadius: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom align="center">
              {getReviewScreenTitle()}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }} align="center">
              {getReviewScreenDescription()}
            </Typography>
            
            {/* Main action button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleEditData}
                startIcon={<PiPencilSimple />}
                sx={{ minWidth: 200 }}
              >
                {getReviewButtonText()}
              </Button>
            </Box>
            
            {/* Secondary actions row - schema-driven */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              {/* Download JSON Button - shown when step_controls.allow_intermediate_download is true */}
              {stepControls.allowIntermediateDownload && getJsonOutputFile() && (
                <Button
                  variant="outlined"
                  startIcon={<PiDownloadSimple />}
                  onClick={handleDownloadJson}
                  size="small"
                >
                  {stepControls.downloadButtonText}
                </Button>
              )}
              
              {/* Upload JSON Button - shown when step_controls.allow_manual_trigger is true (can upload your own JSON) */}
              {stepControls.allowManualTrigger && (
                <Button
                  variant="outlined"
                  startIcon={<PiCloudArrowUp />}
                  onClick={handleUploadJsonClick}
                  size="small"
                >
                  Upload JSON
                </Button>
              )}
              
              {/* Skip Review / Direct Trigger Button - only when manual trigger allowed */}
              {stepControls.allowManualTrigger && getJsonOutputFile() && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleSubmitToStepTwo}
                  size="small"
                >
                  Skip Review & Continue
                </Button>
              )}
            </Box>
          </Box>
        )}

        {/* Output files - for non-multi-step workflows or after completion */}
        {outputFiles.length > 0 && !canShowReviewScreen() && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Output Files
            </Typography>
            <Grid container spacing={2}>
              {outputFiles.map((file: any, index: number) => {
                return (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card variant="outlined" sx={{ cursor: 'pointer' }} onClick={() => handleDownload(file)}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PiPencilSimple size={20} style={{ opacity: 0.6 }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {getDownloadFileName(file)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {file.size ? `${(file.size / 1024).toFixed(1)} KB` : 'Click to download'}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
            
            {/* Manual Step 2 Trigger - for multi-step workflows without review (like airplane lease) */}
            {isMultiStepWorkflow() && stepControls.allowManualTrigger && !canShowReviewScreen() && 
             actualExecutionStatus === 'SUCCEEDED' && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={getJsonOutputFile() ? handleSubmitToStepTwo : handleTriggerStepTwoWithFiles}
                  startIcon={<PiPlay />}
                >
                  {stepControls.triggerButtonText || 'Continue to Next Step'}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* Inline data results (e.g. cortese_list matches) for workflows that produce no files */}
        {stepData && (actualExecutionStatus === 'SUCCEEDED' || actualExecutionStatus === 'COMPLETED') && !canShowReviewScreen() && (
          <WorkflowResultData stepData={stepData} />
        )}

        {/* Podcast player — shown when post-workflow podcast generation is active */}
        {(status as any)?.podcast && (
          <Box sx={{ mt: 2 }}>
            {(status as any).podcast.status === 'processing' && (
              <PodcastPlayer loading jobName={job.number} />
            )}
            {(status as any).podcast.status === 'complete' && (status as any).podcast.audio_url && (
              <PodcastPlayer
                audioUrl={(status as any).podcast.audio_url}
                s3Key={(status as any).podcast.s3_key}
                jobName={job.number}
                onRefreshUrl={async () => {
                  const s3Key = (status as any).podcast.s3_key;
                  const response = await apiService.getPodcastAudioUrl(s3Key);
                  return response.presigned_url;
                }}
              />
            )}
            {(status as any).podcast.status === 'failed' && (
              <Typography color="error" variant="body2" sx={{ p: 2 }}>
                Podcast generation failed: {(status as any).podcast.error || 'Unknown error'}
              </Typography>
            )}
          </Box>
        )}

        {actualExecutionStatus === 'RUNNING' && (
          <Box sx={{ mt: 2 }}>
            <Stepper activeStep={getCurrentStep()} alternativeLabel sx={{ mb: 2 }}>
              {executionSteps.map((label, index) => (
                <Step key={label} active={index === getCurrentStep()} completed={index < getCurrentStep()}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <ThemeLoader height={60} />
          </Box>
        )}

        {actualExecutionStatus === 'FAILED' && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Processing failed. Please try again or contact support.
          </Typography>
        )}
        {/* Menu and Dialogs */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          {onRename && (
            <MenuItem onClick={() => { setShowRenameDialog(true); setMenuAnchor(null); }}>
              <PiPencilSimple size={16} style={{ marginRight: 8 }} /> Rename
            </MenuItem>
          )}
          {onDelete && (
            <MenuItem onClick={() => { onDelete(job.jobId); setMenuAnchor(null); }} sx={{ color: 'error.main' }}>
              <PiTrash size={16} style={{ marginRight: 8 }} /> Delete
            </MenuItem>
          )}
        </Menu>
        
        <RenameJobDialog
          open={showRenameDialog}
          currentName={jobName}
          onClose={() => setShowRenameDialog(false)}
          onRename={(newName) => onRename?.(job.jobId, newName)}
        />
        
        <Dialog
          open={jsonEditorOpen}
          onClose={() => setJsonEditorOpen(false)}
          maxWidth="xl"
          fullWidth
          PaperProps={{ sx: { height: '95vh' } }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{getReviewScreenTitle()}</span>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, newMode) => {
                if (newMode !== null) {
                  if (newMode === 'json') {
                    // Switching to JSON view - serialize current data
                    setJsonText(JSON.stringify(jsonData, null, 2));
                    setJsonError(null);
                  } else {
                    // Switching to form view - try to parse JSON text
                    try {
                      const parsed = JSON.parse(jsonText);
                      setJsonData(parsed);
                      setJsonError(null);
                    } catch (e) {
                      setJsonError('Invalid JSON - fix errors before switching to form view');
                      return; // Don't switch if JSON is invalid
                    }
                  }
                  setViewMode(newMode);
                }
              }}
              size="small"
            >
              <ToggleButton value="form" aria-label="form view">
                <PiList size={16} style={{ marginRight: 4 }} />
                Form
              </ToggleButton>
              <ToggleButton value="json" aria-label="json view">
                <PiCode size={16} style={{ marginRight: 4 }} />
                JSON
              </ToggleButton>
            </ToggleButtonGroup>
          </DialogTitle>
          <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', height: '100%' }}>
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              {viewMode === 'form' ? (
                <WorkflowReviewForm
                  practice={practice}
                  workflow={actualWorkflow}
                  initialData={jsonData}
                  onDataChange={setJsonData}
                />
              ) : (
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  {jsonError && (
                    <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                      {jsonError}
                    </Typography>
                  )}
                  <TextField
                    multiline
                    fullWidth
                    value={jsonText}
                    onChange={(e) => {
                      setJsonText(e.target.value);
                      // Try to parse and update jsonData in real-time
                      try {
                        const parsed = JSON.parse(e.target.value);
                        setJsonData(parsed);
                        setJsonError(null);
                      } catch (e) {
                        setJsonError('Invalid JSON syntax');
                      }
                    }}
                    sx={{ 
                      flex: 1,
                      '& .MuiInputBase-root': { 
                        height: '100%',
                        alignItems: 'flex-start',
                        fontFamily: 'monospace',
                        fontSize: '13px'
                      },
                      '& .MuiInputBase-input': {
                        height: '100% !important',
                        overflow: 'auto !important'
                      }
                    }}
                    error={!!jsonError}
                  />
                </Box>
              )}
            </Box>
            {showOriginalDocs && (
            <Box sx={{ width: '50%', borderLeft: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                {inputDisplayConfig.panel_title}
              </Typography>
              <Box sx={{ flex: 1, p: 1 }}>
                <PDFViewer status={status} />
              </Box>
            </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setJsonEditorOpen(false)}>
              Cancel
            </Button>
            {stepControls.allowIntermediateEdit && sourceS3Uri && (
              <Button
                onClick={handleSaveReview}
                variant="outlined"
                disabled={saving || !!jsonError}
              >
                {saving ? 'Saving...' : saveSuccess ? 'Saved \u2713' : (reviewFormConfig?.saveButtonText ?? 'Save Progress')}
              </Button>
            )}
            <Button 
              onClick={handleSubmitToStepTwo}
              variant="contained"
              color="primary"
              size="large"
              disabled={!!jsonError}
            >
              {getContinueButtonText()}
            </Button>
          </DialogActions>
        </Dialog>
        

      </CardContent>
    </Card>
  );
}